import type { MockMessage } from "@/dummy";
import type { RealtimeMessageEvent } from "@/lib/use-visitor-realtime";
import {
  type Assignment,
  assignmentFromEvent,
  assignmentSystemRow,
} from "@/lib/visitor-assignment";
import type { WidgetRoute } from "@/lib/widget-route";
import { mapRealtimeInbound } from "./realtime-inbound";

/**
 * The chat whose per-chat stream the tab holds open: the conversation on screen, only while the panel is
 * open. The anchor thread (no chatId on the route) is the session chat; a topic thread that does not exist
 * yet (no chatId) has no stream. Anything else (list, Home, Help, Profile, closed panel) is `null`, which
 * closes the stream: the threads stream alone covers badge, peek, sound and notifications.
 */
export function openConversationChatId(args: {
  isOpen: boolean;
  route: WidgetRoute;
  anchorChatId: string | null | undefined;
}): string | null {
  const { isOpen, route, anchorChatId } = args;
  if (!isOpen || route.kind !== "messages-thread" || route.pendingLatest) return null;
  return route.chatId ?? (route.threadKey ? null : anchorChatId || null);
}

export interface ConversationSinks {
  /** The mounted MessagesTab handler (typing, read receipts, bodies). */
  forward: (event: RealtimeMessageEvent) => void;
  setAssignment: (next: Assignment) => void;
  appendMessage: (msg: MockMessage) => void;
  updateMessage: (msg: MockMessage) => void;
  deleteMessage: (id: string) => void;
  setContactBlock: (blocked: boolean) => void;
}

/**
 * Feed ONE frame of the open conversation's stream into the view: bubbles, edits, deletes, typing, read
 * receipts, the handoff banner and system rows, and a CONTACT-scope block. It never plays a sound or
 * notifies: that comes from the threads stream.
 */
export function routeConversationEvent(
  event: RealtimeMessageEvent,
  sinks: ConversationSinks,
  aiName: string,
): void {
  if (event.deleted) {
    sinks.deleteMessage(event.externalMessageId);
    return;
  }
  sinks.forward(event);
  if (event.kind === "typing" || event.kind === "read-receipt") return;
  if (event.kind === "assignment") {
    const next = assignmentFromEvent(event);
    if (next) sinks.setAssignment(next);
    const row = assignmentSystemRow(event);
    if (row) sinks.appendMessage(row);
    return;
  }
  // A conversation-status change drives the composer lock via the threads stream; never a bubble.
  if (event.kind === "status") return;
  if (event.kind === "blocked") {
    if (event.blockScope === "CONTACT" && typeof event.blocked === "boolean") {
      sinks.setContactBlock(event.blocked);
    }
    return;
  }
  const msg = mapRealtimeInbound(event, aiName);
  if (!msg) return; // the visitor's own echo, or nothing renderable
  if (event.update) sinks.updateMessage(msg);
  else sinks.appendMessage(msg);
}

/**
 * After a gap: refetch the open conversation's handoff state and apply it. The flag that marks the live
 * status as authoritative is cleared first, otherwise the seed effect would keep ignoring server truth
 * because of an event received before the gap.
 */
export async function resyncAssignment(args: {
  refetch: () => Promise<Assignment | null>;
  apply: (next: Assignment) => void;
  touchedRef: { current: boolean };
}): Promise<void> {
  args.touchedRef.current = false;
  try {
    const next = await args.refetch();
    if (next) args.apply(next);
  } catch {
    // Offline again: the next reopen retries.
  }
}
